import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Trilingual labels for the general-inquiry form. Kept here so both the
// /contact page and any embed (e.g. the "Looking for a custom desert
// experience?" CTA on /experiences) share one source of truth.
const COPY = {
  name:    { en: "Your name",    fr: "Votre nom",     es: "Su nombre" },
  email:   { en: "Your email",   fr: "Votre e-mail",  es: "Su correo" },
  subject: { en: "Subject",      fr: "Sujet",         es: "Asunto" },
  message: { en: "Your message", fr: "Votre message", es: "Su mensaje" },
  send:    { en: "Send message", fr: "Envoyer",       es: "Enviar mensaje" },
  success: {
    en: "Thank you. We'll be in touch within 24 hours.",
    fr: "Merci. Nous revenons vers vous sous 24 heures.",
    es: "Gracias. Le respondemos en 24 horas.",
  },
  error: {
    en: "Sorry — something went wrong. Try again or email us directly.",
    fr: "Désolé — une erreur est survenue. Réessayez ou écrivez-nous directement.",
    es: "Lo sentimos — algo ha fallado. Reintente o escríbanos directamente.",
  },
};

// Shared general-inquiry form. POSTs to /api/contact like the legacy form.
// Supports an optional `variant="dark"` styling for placement against the
// editorial dark backgrounds used by the bespoke section.
//
// Props:
//   prefillSubject / prefillMessage — auto-populate from URL or section context
//   variant — "light" (default, on cream/white) or "dark" (on near-black hero)
//   testid  — prefix for data-testid attributes (default: "inquiry")
const GeneralInquiryForm = ({
  prefillSubject = "",
  prefillMessage = "",
  variant = "light",
  testid = "inquiry",
}) => {
  const { lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefillSubject || prefillMessage) {
      setForm((p) => ({
        ...p,
        subject: p.subject || prefillSubject || "",
        message: p.message || prefillMessage || "",
      }));
    }
  }, [prefillSubject, prefillMessage]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(pick(COPY.success, lang));
      setForm({ name: "", email: "", subject: prefillSubject || "", message: "" });
    } catch {
      toast.error(pick(COPY.error, lang));
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const labelCls = `block overline mb-2 ${isDark ? "!text-[#FDFBF7]/70" : "!text-[#5C5248]"}`;
  const inputCls = `w-full bg-transparent outline-none px-4 py-3 text-base transition-colors ${
    isDark
      ? "border border-[#FDFBF7]/25 focus:border-[#D4A373] text-[#FDFBF7] placeholder:text-[#FDFBF7]/40"
      : "border border-[#2C2621]/20 focus:border-[#C16542] text-[#2C2621]"
  }`;
  const btnCls = isDark
    ? "bg-[#D4A373] hover:bg-[#C2925E] text-[#1A1513]"
    : "bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7]";

  return (
    <form onSubmit={submit} className="space-y-6 max-w-3xl" data-testid={`${testid}-form`}>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>{pick(COPY.name, lang)}</label>
          <input
            required
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            data-testid={`${testid}-name-input`}
          />
        </div>
        <div>
          <label className={labelCls}>{pick(COPY.email, lang)}</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
            data-testid={`${testid}-email-input`}
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>{pick(COPY.subject, lang)}</label>
        <input
          type="text"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          className={inputCls}
          data-testid={`${testid}-subject-input`}
        />
      </div>
      <div>
        <label className={labelCls}>{pick(COPY.message, lang)}</label>
        <textarea
          required
          rows={6}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className={inputCls}
          data-testid={`${testid}-message-input`}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className={`disabled:opacity-60 px-10 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 transition-colors ${btnCls}`}
        data-testid={`${testid}-submit-button`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
        ) : (
          <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
        )}
        {pick(COPY.send, lang)}
      </button>
    </form>
  );
};

export default GeneralInquiryForm;
